// AG Grid cell editor adapter that reuses the searchable-select Stimulus controller.
//
// Creates a minimal root element with data attributes and lets the Stimulus
// controller self-render its dropdown structure on connect (embedded mode).
// This keeps the cell editor as a thin AG Grid adapter — all dropdown/form
// rendering is centralized in the Stimulus controller.
//
// Used via a behavior's `cellEditor` key:
//   cellEditor: SearchableCellEditor
//   cellEditorParams: {
//     url: "/api/locations",
//     storageKey: "locations",
//     valueField: "locationId",
//     forms: [
//       { label: "New Location", modalId: "location_slideout", url: "/locations/new?slideout=true" },
//       { label: "Edit", modalId: "location_slideout", urlTemplate: "/locations/:id/edit?slideout=true" }
//     ]
//   }
//   cellEditorPopup: true

export class SearchableCellEditor {
  init(params) {
    this.params = params
    this.value = params.value || ""
    // AG Grid consumes the key that starts editing, so seed searchable editors with it.
    this.startedWithCharacter = typeof params.eventKey === "string" && params.eventKey.length === 1
    this.pendingSearch = this.startedWithCharacter ? params.eventKey : null

    // Stimulus renders the input asynchronously. Buffer any characters typed before
    // it is attached so rapid typing cannot send them back to the grid.
    if (this.startedWithCharacter) {
      this.capturePendingCharacter = (event) => {
        const isCharacter = typeof event.key === "string" && event.key.length === 1
        if (!isCharacter || event.ctrlKey || event.metaKey || event.altKey) return

        this.pendingSearch += event.key
        event.preventDefault()
        event.stopPropagation()
      }
      document.addEventListener("keydown", this.capturePendingCharacter, true)
    }

    const ep = params.colDef.cellEditorParams || {}

    // Save a reference to the grid controller element before AG Grid can destroy us.
    // Used to dispatch events that trigger autosave after add-new creation.
    this.gridElement = params.eGridCell?.closest('[data-controller~="grid"]')

    // Current value ID — used by #updateEditButton to show/hide edit buttons
    const valueId = ep.valueField ? (params.data[ep.valueField] || "") : ""

    // Build a minimal root element — the Stimulus controller handles all internal rendering.
    this.gui = document.createElement("div")
    this.gui.setAttribute("data-controller", "searchable-select")
    this.gui.setAttribute("data-searchable-select-url-value", ep.url || "")
    this.gui.setAttribute("data-searchable-select-storage-key-value", ep.storageKey || "")
    this.gui.setAttribute("data-searchable-select-embedded-value", "true")
    this.gui.setAttribute("data-searchable-select-min-chars-value", "0")
    this.gui.setAttribute("data-searchable-select-debounce-value", "200")
    if (ep.allowCustom) {
      this.gui.setAttribute("data-searchable-select-allow-custom-value", "true")
    }
    this.gui.setAttribute("data-searchable-select-initial-value-value", String(valueId))
    this.gui.setAttribute(
      "data-searchable-select-initial-search-value",
      this.startedWithCharacter ? this.pendingSearch : this.value
    )
    this.gui.setAttribute("data-searchable-select-forms-value", JSON.stringify(ep.forms || []))
    this.gui.style.width = "240px"

    // Prevent clicks inside from closing the AG Grid editor
    this.gui.addEventListener("mousedown", (e) => e.preventDefault())

    // Listen for selection from the Stimulus controller
    this.gui.addEventListener("searchable-select:change", (e) => {
      this.value = e.detail.label

      if (ep.valueField) {
        this.params.data[ep.valueField] = e.detail.value
      }

      this.params.stopEditing()
    })

    // Listen for dismiss (Escape key) from the Stimulus controller
    this.gui.addEventListener("searchable-select:dismiss", () => {
      this.params.stopEditing()
    })

    // Listen for add-new / edit from the Stimulus controller.
    // Register a pending assignment with the grid controller so the
    // record_sync module can handle it when record-created fires.
    if (ep.forms && ep.forms.length > 0) {
      this.gui.addEventListener("searchable-select:add-new", () => {
        if (this.gridElement) {
          this.gridElement.dispatchEvent(new CustomEvent("grid:pendingAssignment", {
            detail: {
              rowId: String(this.params.data.id),
              field: this.params.colDef.field,
              valueField: ep.valueField,
              storageKey: ep.storageKey
            }
          }))
        }
        this.params.stopEditing()
      })
    }
  }

  getGui() {
    return this.gui
  }

  afterGuiAttached() {
    // The Stimulus controller handles focus in connect() → open().
    // Use rAF as a fallback in case Stimulus connects slightly late.
    requestAnimationFrame(() => {
      const search = this.gui.querySelector('[data-searchable-select-target="search"]')
      if (search) {
        search.focus()
        if (this.startedWithCharacter) {
          this.stopCapturingCharacters()
          search.value = this.pendingSearch
          search.setSelectionRange(search.value.length, search.value.length)
          search.dispatchEvent(new Event("input", { bubbles: true }))
        } else {
          search.select()
        }
      }
    })
  }

  getValue() {
    return this.value
  }

  isPopup() {
    return true
  }

  destroy() {
    this.stopCapturingCharacters()
    // Stimulus disconnects automatically when AG Grid removes the element.
    // The add-new listener (if active) lives on document and self-cleans.
  }

  stopCapturingCharacters() {
    if (!this.capturePendingCharacter) return

    document.removeEventListener("keydown", this.capturePendingCharacter, true)
    this.capturePendingCharacter = null
  }
}
